import {
  ActionBarPrimitive,
  AuiIf,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAssistantRuntime,
  useAuiState,
} from '@assistant-ui/react'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BrainIcon,
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  RefreshCwIcon,
  SquareIcon,
} from 'lucide-react'
import { Component, type FC, type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AgentComposerControls } from '@/components/agent/AgentToolsPanel'
import { AgentWelcomeView } from '@/components/agent/AgentWelcomeView'
import { ToolCallBlock } from '@/components/agent/ToolCallBlock'
import { MarkdownText } from '@/components/assistant-ui/markdown-text'
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useAuth } from '@/contexts/AuthContext'
import { useThreadBusy } from '@/features/agent/AgentActivityContext'
import { AgentActivityRow } from '@/features/agent/AgentActivityRow'
import { useAgentSuggestions } from '@/features/agent/AgentSuggestionsContext'
import { FailedSendRetry } from '@/features/agent/FailedSendRetry'
import {
  HistoryLoadMoreSentinel,
  useHistoryPagination,
} from '@/features/agent/HistoryPaginationContext'
import { ThreadStatusBanner } from '@/features/agent/ThreadStatusBanner'
import { useThreadStatus } from '@/features/agent/ThreadStatusContext'
import { isMobileViewport } from '@/hooks/use-mobile'
import { cn } from '@/utils/cn'

interface AgentThreadProps {
  /** Constrains message + composer width; viewport (scrollbar) stays full-width. */
  maxWidth?: string
  /** Background color for the thread root + sticky composer footer. */
  background?: 'card' | 'background'
  /** Optional small hint row rendered under the composer (shortcuts + context). */
  composerFooter?: ReactNode
  controls?: { disabled: boolean; placeholder: string; hideTools?: boolean }
}

export const AgentThread: FC<AgentThreadProps> = ({
  maxWidth = '100%',
  background = 'card',
  composerFooter,
  controls,
}) => {
  const bg = background === 'background' ? 'bg-background' : 'bg-card'
  // Disable autoScroll for the brief window we're paginating older
  // history so assistant-ui's anchor pass doesn't fight our scroll
  // restore in AgentChatHost.
  const isPaging = useHistoryPagination()?.isPaging ?? false
  return (
    <ThreadPrimitive.Root
      className={cn('aui-root aui-thread-root @container flex h-full flex-col', bg)}
      style={{
        ['--thread-max-width' as string]: maxWidth,
        ['--composer-radius' as string]: '16px',
        ['--composer-padding' as string]: '8px',
      }}
    >
      {/* turnAnchor deliberately omitted (defaults to "bottom"): the classic
          stick-to-bottom model is what ViewportScrollOwner asserts; "top"
          anchoring contradicts it (it implies autoScroll off + a below-turn
          spacer) and was the source of mixed scroll behavior. */}
      <ThreadPrimitive.Viewport
        autoScroll={!isPaging}
        className="aui-thread-viewport relative flex flex-1 flex-col overflow-x-hidden overflow-y-auto px-4 pt-4 md:px-3 md:pt-8"
      >
        <ViewportScrollOwner />
        <AuiIf condition={s => s.thread.isEmpty}>
          <AgentWelcome />
        </AuiIf>

        <HistoryLoadMoreSentinel />

        <ThreadPrimitive.Messages>{() => <AgentThreadMessage />}</ThreadPrimitive.Messages>

        {/* Edge-to-edge composer dock. The negative margin cancels the
            viewport's horizontal padding so the background + top border span
            the full width; the inner padding restores the content gutter, and
            an inner centered container keeps the composer at thread width.
            Below `lg` the dock is lifted (`z-20`) above the sticky table
            headers (z-10) of tool cards scrolling underneath it. */}
        <ThreadPrimitive.ViewportFooter
          className={cn(
            'aui-thread-viewport-footer sticky bottom-0 z-20 -mx-4 mt-auto flex flex-col overflow-visible border-t border-border px-4 pt-3 pb-3 md:-mx-3 md:px-3 md:pt-5 lg:z-auto',
            bg
          )}
        >
          <div className="mx-auto flex w-full max-w-(--thread-max-width) flex-col gap-3">
            <AgentScrollToBottom />
            <ThreadStatusBanner />
            <AgentActivityRow />
            <AgentComposer controls={controls} />
            {/* Control pills lead the hints row; the page-provided footer
                (shortcut hints + token indicator) fills the rest. basis-56
                makes the footer wrap to its own full-width line in narrow
                side-chats instead of getting squeezed and clipped. On a phone
                the smaller basis keeps the hints beside the pills at 360 and
                430 px too, instead of spending a second row on them. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {!controls?.hideTools && <AgentComposerControls />}
              {composerFooter && (
                <div className="min-w-0 flex-1 basis-32 md:basis-56">{composerFooter}</div>
              )}
            </div>
          </div>
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  )
}

/** Owns scroll-to-bottom at the two moments assistant-ui misses: first paint
 * (its `thread.initialize` scroll fires before our history rows exist) and run
 * start (the transport runtime's send isn't reliably followed by a scroll).
 * Also dispatches a synthetic scroll event after each assert — the library only
 * recomputes its `isAtBottom` tracking inside scroll handlers, so an untouched
 * viewport otherwise never engages stick-to-bottom. */
const ViewportScrollOwner: FC = () => {
  const isRunning = useAuiState(s => s.thread.isRunning)
  const isPaging = useHistoryPagination()?.isPaging ?? false
  const wasRunningRef = useRef(false)

  const assertBottom = useCallback(() => {
    const viewport = document.querySelector<HTMLElement>('.aui-thread-viewport')
    if (!viewport) return
    viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'instant' })
    // Already-at-bottom asserts produce no scroll event; fire one so the
    // library still seeds isAtBottom from the real position.
    viewport.dispatchEvent(new Event('scroll'))
  }, [])

  // First paint: land on the latest message. Double-rAF lets markdown and
  // tool-card rows lay out before we measure scrollHeight.
  useEffect(() => {
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(assertBottom)
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [assertBottom])

  // Send edge: starting a turn always returns the viewport to the bottom
  // (skipped while history pagination owns the scroll position).
  useEffect(() => {
    const started = isRunning && !wasRunningRef.current
    wasRunningRef.current = isRunning
    if (started && !isPaging) requestAnimationFrame(assertBottom)
  }, [isRunning, isPaging, assertBottom])

  return null
}

/** A render crash in one message degrades to a one-line notice instead of
 * unmounting the whole thread (the old failure mode was React Router's raw
 * "Unexpected Application Error!" page). */
class MessageRenderBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: unknown) {
    console.error('[agent] message render failed:', error)
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

const AgentThreadMessage: FC = () => {
  const role = useAuiState(s => s.message?.role)
  const schemaContext = useAuiState(s =>
    s.message?.content.some(
      part =>
        part.type === 'text' &&
        part.text.startsWith('Reference files (') &&
        part.text.includes('Structured intake (authoritative):')
    )
  )

  const { t } = useTranslation()
  if (role === 'user' && schemaContext)
    return (
      <p className="px-3 py-2 text-xs text-muted-foreground">
        {t('schemaAgent.workbench.referenceContext')}
      </p>
    )
  return (
    <div className="mx-auto w-full max-w-(--thread-max-width)">
      <MessageRenderBoundary
        fallback={
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-muted-foreground text-xs">
            {t('chat.messageRenderFailed')}
          </div>
        }
      >
        {role === 'user' ? <AgentUserMessage /> : <AgentAssistantMessage />}
      </MessageRenderBoundary>
    </div>
  )
}

const AgentWelcome: FC = () => {
  const { suggestions, welcomeText } = useAgentSuggestions()
  const runtime = useAssistantRuntime()

  return (
    <AgentWelcomeView
      welcomeText={welcomeText}
      suggestions={suggestions}
      onSelectSuggestion={prompt =>
        runtime.thread.append({ role: 'user', content: [{ type: 'text', text: prompt }] })
      }
    />
  )
}

const AgentScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="absolute -top-10 z-10 self-center rounded-full p-3 max-md:size-9 disabled:invisible dark:border-border dark:bg-background"
      >
        <ArrowDownIcon className="size-3.5" />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  )
}

const AgentComposer: FC<{ controls?: AgentThreadProps['controls'] }> = ({ controls }) => {
  const { status } = useThreadStatus()
  const isFull = status?.kind === 'full' || controls?.disabled
  // Unprompted focus (mount, run start, scroll to bottom, thread switch) pops
  // the on-screen keyboard over the thread, so a phone focuses only on tap.
  const [autoFocus] = useState(() => !isMobileViewport())
  return (
    <ComposerPrimitive.Root className="relative flex w-full flex-col">
      <Card className="flex w-full flex-row items-center gap-2 rounded-xl p-0 px-3 py-2 transition-colors focus-within:border-ring/60">
        <ComposerPrimitive.Input
          placeholder={
            controls?.placeholder ??
            (isFull ? 'Thread is full — start a new conversation' : 'Ask about this case...')
          }
          className="min-h-6 max-h-24 flex-1 resize-none bg-transparent text-base outline-none placeholder:text-muted-foreground/80 disabled:cursor-not-allowed md:min-h-5 md:max-h-10 md:text-sm"
          rows={1}
          autoFocus={autoFocus}
          unstable_focusOnRunStart={autoFocus}
          unstable_focusOnScrollToBottom={autoFocus}
          unstable_focusOnThreadSwitched={autoFocus}
          aria-label="Message input"
          disabled={isFull}
        />
        <div className="flex items-center shrink-0">
          <AuiIf condition={s => !s.thread.isRunning}>
            <ComposerPrimitive.Send asChild>
              <TooltipIconButton
                tooltip={
                  controls?.disabled
                    ? controls.placeholder
                    : isFull
                      ? 'Thread is full — start a new conversation'
                      : 'Send'
                }
                side="bottom"
                type="button"
                variant="default"
                size="icon"
                className="size-9 rounded-full md:size-7"
                disabled={isFull}
              >
                <ArrowUpIcon className="size-3.5" />
              </TooltipIconButton>
            </ComposerPrimitive.Send>
          </AuiIf>
          <AuiIf condition={s => s.thread.isRunning}>
            <ComposerPrimitive.Cancel asChild>
              <Button
                type="button"
                variant="default"
                size="icon"
                className="size-9 rounded-full md:size-7"
              >
                <SquareIcon className="size-2.5 fill-current" />
              </Button>
            </ComposerPrimitive.Cancel>
          </AuiIf>
        </div>
      </Card>
    </ComposerPrimitive.Root>
  )
}

const AgentAssistantMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      className="fade-in slide-in-from-bottom-1 relative w-full animate-in py-2 duration-150"
      data-role="assistant"
    >
      <div className="wrap-break-word text-foreground text-sm leading-relaxed">
        <MessagePrimitive.Parts
          components={{
            Text: MarkdownText,
            Reasoning: ({ text }) => <Reasoning text={text} />,
            tools: { Fallback: ToolCallFallback },
          }}
        />
        <MessagePrimitive.Error>
          <ErrorPrimitive.Root className="mt-1.5 rounded-md border border-destructive bg-destructive/10 p-2 text-destructive text-xs">
            <ErrorPrimitive.Message className="line-clamp-2" />
          </ErrorPrimitive.Root>
          <FailedSendRetry />
        </MessagePrimitive.Error>
      </div>

      <div className="flex h-8 items-center md:h-5">
        <AgentAssistantActionBar />
      </div>
    </MessagePrimitive.Root>
  )
}

const AgentAssistantActionBar: FC = () => {
  // Hidden while the host reports a server-side run too: reloading would post
  // a turn the locked session refuses.
  const busy = useThreadBusy()
  if (busy) return null
  return (
    <ActionBarPrimitive.Root
      autohide="not-last"
      className="-ml-1 flex gap-0.5 text-muted-foreground"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="Copy" className="size-8 md:size-6">
          <AuiIf condition={s => s.message.isCopied}>
            <CheckIcon className="size-3" />
          </AuiIf>
          <AuiIf condition={s => !s.message.isCopied}>
            <CopyIcon className="size-3" />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip="Retry" className="size-8 md:size-6">
          <RefreshCwIcon className="size-3" />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload>
    </ActionBarPrimitive.Root>
  )
}

const AgentUserMessage: FC = () => {
  const { user } = useAuth()
  const initials = user?.username?.slice(0, 2).toUpperCase() || 'U'
  return (
    <MessagePrimitive.Root
      className="fade-in slide-in-from-bottom-1 flex w-full animate-in items-start justify-end gap-2 py-2 duration-150"
      data-role="user"
    >
      <div className="max-w-[85%] rounded-2xl border border-primary/25 bg-primary/5 px-4 py-3 text-foreground text-sm leading-relaxed">
        <MessagePrimitive.Parts />
      </div>
      <Avatar className="h-8 w-8 shrink-0 rounded-full">
        <AvatarFallback className="rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
          {initials}
        </AvatarFallback>
      </Avatar>
    </MessagePrimitive.Root>
  )
}

const ToolCallFallback: FC<{
  toolName: string
  toolCallId: string
  args: Record<string, unknown>
  result?: unknown
  status: { type: string }
}> = ({ toolName, toolCallId, args, result, status }) => {
  const resultText =
    result === undefined
      ? undefined
      : typeof result === 'string'
        ? result
        : JSON.stringify(result, null, 2)
  return (
    <ToolCallBlock
      toolName={toolName}
      toolCallId={toolCallId}
      args={args}
      result={resultText}
      done={status.type !== 'running'}
    />
  )
}

const Reasoning: FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="my-1.5 overflow-hidden rounded-lg border bg-muted/50 text-xs">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded(!expanded)}
        className="h-auto w-full justify-start gap-1.5 rounded-none px-2.5 py-1.5 text-xs font-normal text-muted-foreground hover:text-foreground"
      >
        <BrainIcon className="size-3.5 shrink-0" />
        <span className="font-medium">Reasoning</span>
        <ChevronDownIcon
          className={cn('ml-auto size-3.5 transition-transform', expanded && 'rotate-180')}
        />
      </Button>
      {expanded && (
        <div className="border-t px-2.5 py-2 text-muted-foreground whitespace-pre-wrap">{text}</div>
      )}
    </div>
  )
}
